/**
 * O que os `ia/acoes.js` dos parciais têm em comum — review do PR #2041.
 *
 * A receita "tem router? → `empurrarRota` → `.then(avisarAbaBloqueada)` →
 * `.catch($showSnack com a saída)`" estava escrita em SEIS módulos (crm,
 * atendimento, clients, consolidator, financial-planning, tarefas), e
 * `clienteDoCartao`/`avisar` em três — e as cópias já divergiam em detalhe
 * (uma não guardava `vm` nulo; outra devolvia boolean). O cabeçalho de
 * `atendimento/ia/acoes.js` dizia que "uma implementação só é o que impede a
 * terceira cópia de nascer sem o `catch`" — e nasceram mais quatro. Este
 * arquivo é essa implementação: os `acoes.js` viram wrappers com a rota e a
 * mensagem de cada módulo.
 *
 * `this`/`vm` é a instância do embrulho (`cartao-do-chat.js`): é de onde saem
 * `$router`, `$store`, `$showSnack` e `payloadDaTool`.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import {
  avisarAbaBloqueada,
  empurrarRota,
  temContextoDeNavegacao,
} from './tools-de-front/catalogo'

/**
 * Snack de erro no embrulho — e nada quando a superfície não tem `$showSnack`
 * (o chat também roda embutido em telas sem o mixin). Devolve `null` para
 * encadear como retorno de "não abriu".
 */
export function avisarErro(vm, texto) {
  if (vm && typeof vm.$showSnack === 'function') {
    vm.$showSnack({ text: texto, color: 'error', button: true })
  }
  return null
}

/**
 * Empurra a rota de um cartão, com os dois avisos que todo clique precisa.
 *
 * - Sem router não há o que empurrar (`null`) — um `undefined.push` no clique
 *   derrubaria o render do thread inteiro.
 * - Aba barrada pelo navegador RESOLVE (não rejeita): `avisarAbaBloqueada`
 *   avisa em `warning` e o resultado segue intacto no `.then`.
 * - `empurrarRota` absorve só o `NavigationDuplicated` e RELANÇA o resto
 *   (guarda de rota, rota sem href): o `.catch` avisa com a SAÍDA na mensagem
 *   — um clique que não faz nada é lido como bug da conversa.
 *
 * @param {Object} vm               A instância do embrulho.
 * @param {Object} rota             O objeto de rota (`{ name, params, query }`
 *                                  ou `{ path }`).
 * @param {Object} opcoes
 * @param {string} opcoes.mensagemDeErro  O snack quando a navegação falha —
 *                                  com a saída ("Tente pelo menu …").
 * @returns {Promise<Object|null>|null} `null` sem router; senão a promise que
 *                                  resolve com o resultado de `empurrarRota`
 *                                  (`{ aberto, bloqueado, href }`) ou `null`
 *                                  quando falhou (já avisado).
 */
export function abrirRotaDoCartao(vm, rota, { mensagemDeErro } = {}) {
  const ctx = { router: vm && vm.$router }
  if (!temContextoDeNavegacao(ctx)) return null

  return empurrarRota(ctx, rota)
    .then((resultado) => avisarAbaBloqueada(vm, resultado))
    .catch(() => avisarErro(vm, mensagemDeErro))
}

const preenchido = (valor) =>
  valor !== undefined && valor !== null && valor !== ''

const objetoDoCartao = (vm) => {
  const doCartao = vm && vm.cliente
  const temId =
    doCartao && typeof doCartao === 'object' && preenchido(doCartao.id)
  return temId ? doCartao : null
}

const naCarteiraCarregada = (vm, id) => {
  const getters = (vm && vm.$store && vm.$store.getters) || {}
  const lista = getters['user/allClients']
  if (!Array.isArray(lista)) return null
  return lista.find((c) => c && String(c.id) === String(id)) || null
}

/**
 * De qual cliente é este cartão — `{ cliente, id }`.
 *
 * Duas portas, nesta ordem: o OBJETO que o cartão expõe (`vm.cliente`, com
 * id) e, na falta dele, o `id_cliente_app` do payload da tool, procurado na
 * carteira já carregada (`user/allClients`).
 *
 * ★ O `id` vem preenchido mesmo quando o objeto não está na carteira (lista
 * ainda carregando, cliente fora da página): quem só precisa do id para a
 * rota (deep link que hidrata pelo param) navega; quem precisa do objeto
 * (`enterClient`) decide pelo `cliente`. Cada módulo escolhe — é o que
 * separa `clients` (id basta) de `financial-planning` (dispatch só com o
 * objeto).
 */
export function clienteDoCartao(vm) {
  const doCartao = objetoDoCartao(vm)
  if (doCartao) return { cliente: doCartao, id: doCartao.id }

  const payload = (vm && vm.payloadDaTool) || {}
  const id = payload.id_cliente_app
  if (!preenchido(id)) return { cliente: null, id: null }

  return { cliente: naCarteiraCarregada(vm, id), id: id }
}
